import { useDraft } from "../state/useDraft";
import type { RecurrenceSpec, Series, Unit } from "../api/generated";
import {
  NO_RECURRENCE,
  UNITS,
  describeEnd,
  describeRule,
  draftFromSeries,
  specOf,
  type EndDraft,
  type RecurrenceDraft,
} from "../domain/recurrence";

interface RecurrenceEditorProps {
  series: Series | undefined;
  onApply(change: RecurrenceSpec | "Stop"): void;
}

type RepeatingDraft = Exclude<RecurrenceDraft, { kind: "none" }>;

const DEFAULT_REPEATING: RepeatingDraft = {
  kind: "every",
  count: 1,
  unit: "Week",
  end: { kind: "never" },
};

function initialDraft(series: Series | undefined): RecurrenceDraft {
  if (series === undefined || series.ended) return NO_RECURRENCE;
  return draftFromSeries(series.rule, series.end);
}

export function RecurrenceEditor({ series, onApply }: RecurrenceEditorProps) {
  const stored = initialDraft(series);
  const [draft, setDraft] = useDraft<RecurrenceDraft>(stored, JSON.stringify(stored));

  const repeating = draft.kind === "none" ? undefined : draft;
  const changed = JSON.stringify(draft) !== JSON.stringify(stored);
  const incomplete = repeating?.end.kind === "until" && repeating.end.date === "";

  const choose = (kind: RecurrenceDraft["kind"]) => {
    if (kind === "none") setDraft(NO_RECURRENCE);
    else setDraft({ ...(repeating ?? DEFAULT_REPEATING), kind });
  };

  const apply = () => {
    if (repeating === undefined) onApply("Stop");
    else onApply(specOf(repeating));
  };

  return (
    <section className="panel" aria-labelledby="recurrence-heading">
      <h3 id="recurrence-heading">Repeat</h3>
      {series !== undefined && (
        <p className="field-hint">
          {series.ended ? "Stopped: " : "Now: "}
          {describeRule(series.rule)}
          {describeEnd(series.end) === undefined ? "" : `, ${describeEnd(series.end)}`}
        </p>
      )}
      <div className="field">
        <label htmlFor="repeat-kind">Repeats</label>
        <select
          id="repeat-kind"
          className="select"
          value={draft.kind}
          onChange={(event) => choose(event.target.value as RecurrenceDraft["kind"])}
        >
          <option value="none">Does not repeat</option>
          <option value="every">Every (fixed schedule)</option>
          <option value="after">After completion</option>
        </select>
      </div>
      {repeating !== undefined && (
        <>
          <div className="field-row">
            <div className="field">
              <label htmlFor="repeat-count">Interval</label>
              <input
                id="repeat-count"
                className="text-input"
                type="number"
                min={1}
                value={repeating.count}
                onChange={(event) =>
                  setDraft({ ...repeating, count: Math.max(1, Number(event.target.value) || 1) })
                }
              />
            </div>
            <div className="field">
              <label htmlFor="repeat-unit">Unit</label>
              <select
                id="repeat-unit"
                className="select"
                value={repeating.unit}
                onChange={(event) => setDraft({ ...repeating, unit: event.target.value as Unit })}
              >
                {UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit.toLowerCase()}s
                  </option>
                ))}
              </select>
            </div>
          </div>
          <EndEditor end={repeating.end} onChange={(end) => setDraft({ ...repeating, end })} />
        </>
      )}
      <button type="button" className="button" disabled={!changed || incomplete} onClick={apply}>
        {series === undefined || series.ended ? "Set repeat" : "Update this and future"}
      </button>
    </section>
  );
}

function EndEditor({ end, onChange }: { end: EndDraft; onChange(end: EndDraft): void }) {
  return (
    <div className="field-row">
      <div className="field">
        <label htmlFor="repeat-end">Ends</label>
        <select
          id="repeat-end"
          className="select"
          value={end.kind}
          onChange={(event) => {
            const kind = event.target.value as EndDraft["kind"];
            if (kind === "never") onChange({ kind });
            else if (kind === "count") onChange({ kind, times: 10 });
            else onChange({ kind, date: "" });
          }}
        >
          <option value="never">Never</option>
          <option value="until">On a date</option>
          <option value="count">After a number of times</option>
        </select>
      </div>
      {end.kind === "until" && (
        <div className="field">
          <label htmlFor="repeat-until">Last date</label>
          <input
            id="repeat-until"
            className="text-input"
            type="date"
            value={end.date}
            onChange={(event) => onChange({ kind: "until", date: event.target.value })}
          />
        </div>
      )}
      {end.kind === "count" && (
        <div className="field">
          <label htmlFor="repeat-times">Times</label>
          <input
            id="repeat-times"
            className="text-input"
            type="number"
            min={1}
            value={end.times}
            onChange={(event) =>
              onChange({ kind: "count", times: Math.max(1, Number(event.target.value) || 1) })
            }
          />
        </div>
      )}
    </div>
  );
}
